import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import type { TextAreaChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Hint } from "@tql/ui-components-react/kendo/labels";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Form, FormElement } from "@tql/ui-components-react/tql/form/beta";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ConsentStatement } from "~/shared/components/consent-statement/consent-statement.tsx";
import {
	FieldType,
	ValidationInputField,
} from "~/shared/components/fields/validation-input-field.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useGetCurrentUserInfo } from "~/shared/hooks/use-current-user-info.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";

import {
	useGetBillingInformation,
	useStates,
	useUpdateBillingInformation,
} from "./hooks/use-billing-information.ts";
import styles from "./index.module.css";
import type { UpdateBillingInformationBody } from "./schemas/billing-information-schema.ts";
import {
	booleanDropdownOptions,
	reimburseUnloadingOptions,
} from "./schemas/billing-information-schema.ts";

const requiredFieldValidator = (value: string | undefined) =>
	value && value.trim() !== "" ? "" : "This field is required.";

const requiredDropdownValidator = (value: unknown) =>
	value !== undefined && value !== null && value !== ""
		? ""
		: "This field is required.";

type FormValues = Record<string, unknown>;

const isEmptyString = (value: unknown) =>
	!value || (typeof value === "string" && value.trim() === "");

const validateRequiredAddress = (
	prefix: "mailing" | "billing",
	errors: Record<string, string>,
	values: FormValues,
) => {
	if (isEmptyString(values[`${prefix}StreetAdd`]))
		errors[`${prefix}StreetAdd`] = "Street Address is required.";
	if (isEmptyString(values[`${prefix}CityAdd`]))
		errors[`${prefix}CityAdd`] = "City is required.";
	if (!values[`${prefix}StateAdd`])
		errors[`${prefix}StateAdd`] = "State is required.";
	if (isEmptyString(values[`${prefix}ZipAdd`]))
		errors[`${prefix}ZipAdd`] = "Zip is required.";
};

const formValidator = (values: FormValues) => {
	const errors: Record<string, string> = {};

	if (isEmptyString(values["companyName"]))
		errors["companyName"] = "Company Name is required.";

	validateRequiredAddress("mailing", errors, values);

	if (!values["billingInfoSame"]) {
		validateRequiredAddress("billing", errors, values);
	}

	if (!values["invoicedVia"])
		errors["invoicedVia"] = "Invoiced Via is required.";
	if (
		values["invoicedVia"] === "Email" &&
		isEmptyString(values["invoicedViaEmail"])
	)
		errors["invoicedViaEmail"] = "Email is required.";
	if (
		values["invoicedVia"] === "Fax" &&
		isEmptyString(values["invoicedViaFax"])
	)
		errors["invoicedViaFax"] = "Fax is required.";

	if (!values["reimburseUnloading"])
		errors["reimburseUnloading"] = "This field is required.";
	if (values["requireBOL"] === undefined || values["requireBOL"] === null)
		errors["requireBOL"] = "This field is required.";
	if (
		values["requireLoadTenders"] === undefined ||
		values["requireLoadTenders"] === null
	)
		errors["requireLoadTenders"] = "This field is required.";

	return errors;
};

type AccountSettingsAddressProps = {
	disabled?: boolean;
	fieldPrefix: "mailing" | "billing";
	loading?: boolean;
	stateData: string[];
};

const AccountSettingsAddress = ({
	disabled = false,
	fieldPrefix,
	loading,
	stateData,
}: AccountSettingsAddressProps) => {
	const streetName = `${fieldPrefix}StreetAdd`;
	const cityName = `${fieldPrefix}CityAdd`;
	const stateName = `${fieldPrefix}StateAdd`;
	const zipName = `${fieldPrefix}ZipAdd`;

	return (
		<div>
			<div className={styles.accountSettingsRow}>
				<ValidationInputField
					disabled={disabled}
					fieldType={FieldType.INPUT}
					id={"companyName"}
					label={"Company Name"}
					loading={loading}
					name={"companyName"}
					required={true}
					validator={requiredFieldValidator}
					wrapperStyle={{ minWidth: 270 }}
				/>
			</div>

			<div className={styles.accountSettingsRow}>
				<ValidationInputField
					disabled={disabled}
					fieldType={FieldType.TEXTBOX}
					id={streetName}
					label={"Street Address"}
					loading={loading}
					name={streetName}
					required={true}
					validator={requiredFieldValidator}
					wrapperStyle={{ flex: 2, minWidth: 120 }}
				/>
				<ValidationInputField
					disabled={disabled}
					fieldType={FieldType.TEXTBOX}
					id={cityName}
					label={"City"}
					loading={loading}
					name={cityName}
					required={true}
					validator={requiredFieldValidator}
					wrapperStyle={{ flex: 1.5, minWidth: 100 }}
				/>
				<ValidationInputField
					data={stateData}
					disabled={disabled}
					fieldType={FieldType.DROPDOWN}
					id={stateName}
					label={"State"}
					loading={loading}
					name={stateName}
					required={true}
					validator={requiredDropdownValidator}
					wrapperStyle={{ flex: 1, minWidth: 80 }}
				/>
				<ValidationInputField
					disabled={disabled}
					fieldType={FieldType.TEXTBOX}
					id={zipName}
					label={"Zip"}
					loading={loading}
					name={zipName}
					required={true}
					validator={requiredFieldValidator}
					wrapperStyle={{ flex: 1, minWidth: 80 }}
				/>
			</div>
		</div>
	);
};

const BillingAddressSync = ({
	formRenderProps,
}: {
	formRenderProps: FormRenderProps;
}) => {
	const billingInfoSame = formRenderProps.valueGetter(
		"billingInfoSame",
	) as boolean;
	const mailingStreet = formRenderProps.valueGetter(
		"mailingStreetAdd",
	) as string;
	const mailingCity = formRenderProps.valueGetter("mailingCityAdd") as string;
	const mailingState = formRenderProps.valueGetter("mailingStateAdd") as string;
	const mailingZip = formRenderProps.valueGetter("mailingZipAdd") as string;

	const prevSame = useRef(billingInfoSame);

	useEffect(() => {
		if (!billingInfoSame) {
			prevSame.current = false;
			return;
		}

		const shouldSync =
			!prevSame.current ||
			formRenderProps.valueGetter("billingStreetAdd") !== mailingStreet ||
			formRenderProps.valueGetter("billingCityAdd") !== mailingCity ||
			formRenderProps.valueGetter("billingStateAdd") !== mailingState ||
			formRenderProps.valueGetter("billingZipAdd") !== mailingZip;

		if (shouldSync) {
			formRenderProps.onChange("billingStreetAdd", { value: mailingStreet });
			formRenderProps.onChange("billingCityAdd", { value: mailingCity });
			formRenderProps.onChange("billingStateAdd", { value: mailingState });
			formRenderProps.onChange("billingZipAdd", { value: mailingZip });
		}
		prevSame.current = true;
	}, [
		billingInfoSame,
		mailingStreet,
		mailingCity,
		mailingState,
		mailingZip,
		formRenderProps,
	]);

	return null;
};

const ADDRESS_EDIT_ROLES = new Set(["Admin", "Operations", "Finance"]);
const BILLING_SETTINGS_EDIT_ROLES = new Set(["Admin", "Operations", "Finance"]);

export const AccountSettings = () => {
	const { isInternal } = useDeploymentType();
	const [miscReqLength, setMiscReqLength] = useState("");
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const miscReqMaxLength = 1000;

	const { data: currentUser, isLoading: isUserLoading } =
		useGetCurrentUserInfo(isInternal);
	const userRole = currentUser?.role ?? "";
	const canEditAddresses = ADDRESS_EDIT_ROLES.has(userRole);
	const canEditBillingSettings = BILLING_SETTINGS_EDIT_ROLES.has(userRole);

	const { data: billingInfo, isLoading: isBillingLoading } =
		useGetBillingInformation();
	const { data: states, isLoading: isStatesLoading } = useStates();
	const updateMutation = useUpdateBillingInformation();

	const stateAbbreviations = useMemo(
		() => states?.map((s) => s.abbreviation) ?? [],
		[states],
	);

	const initialValues = useMemo(() => {
		if (!billingInfo) return undefined;
		return {
			...billingInfo,
			invoicedViaEmail: billingInfo.invoicedViaEmail,
			invoicedViaFax: billingInfo.invoicedViaFax,
			reimburseUnloading: reimburseUnloadingOptions.find(
				(o) => o.value === billingInfo.reimburseUnloading,
			),
			requireBOL: booleanDropdownOptions.find(
				(o) => o.value === billingInfo.requireBOL,
			),
			requireLoadTenders: booleanDropdownOptions.find(
				(o) => o.value === billingInfo.requireLoadTenders,
			),
			billingInfoSame:
				billingInfo.mailingStreetAdd === billingInfo.billingStreetAdd &&
				billingInfo.mailingCityAdd === billingInfo.billingCityAdd &&
				billingInfo.mailingStateAdd === billingInfo.billingStateAdd &&
				billingInfo.mailingZipAdd === billingInfo.billingZipAdd,
		};
	}, [billingInfo]);

	const handleMiscReqChange = (e: TextAreaChangeEvent) => {
		setMiscReqLength(e.value);
	};

	const handleSubmit = (dataItem: Record<string, unknown>) => {
		const billingInfoSame = dataItem["billingInfoSame"] as boolean;

		const body: UpdateBillingInformationBody = {
			email: (dataItem["invoicedViaEmail"] as string) ?? "",
			companyName: dataItem["companyName"] as string,
			mailingStreetAdd: dataItem["mailingStreetAdd"] as string,
			mailingCityAdd: dataItem["mailingCityAdd"] as string,
			mailingStateAdd: dataItem["mailingStateAdd"] as string,
			mailingZipAdd: dataItem["mailingZipAdd"] as string,
			billingStreetAdd: billingInfoSame
				? (dataItem["mailingStreetAdd"] as string)
				: (dataItem["billingStreetAdd"] as string),
			billingCityAdd: billingInfoSame
				? (dataItem["mailingCityAdd"] as string)
				: (dataItem["billingCityAdd"] as string),
			billingStateAdd: billingInfoSame
				? (dataItem["mailingStateAdd"] as string)
				: (dataItem["billingStateAdd"] as string),
			billingZipAdd: billingInfoSame
				? (dataItem["mailingZipAdd"] as string)
				: (dataItem["billingZipAdd"] as string),
			invoicedVia: dataItem["invoicedVia"] as string,
			invoicedViaEmail:
				dataItem["invoicedVia"] === "Email"
					? (dataItem["invoicedViaEmail"] as string)
					: "",
			invoicedViaFax:
				dataItem["invoicedVia"] === "Fax"
					? (dataItem["invoicedViaFax"] as string)
					: "",
			requireBOL: (dataItem["requireBOL"] as { value: boolean }).value,
			requireLoadTenders: (dataItem["requireLoadTenders"] as { value: boolean })
				.value,
			reimburseUnloading: (dataItem["reimburseUnloading"] as { value: number })
				.value,
			miscRequirements: (dataItem["miscRequirements"] as string) ?? "",
		};

		updateMutation.mutate(body, {
			onSuccess: (data) => {
				setSuccessMessage(String(data));
				setShowSuccessModal(true);
			},
		});
	};

	const handleCloseSuccessModal = useCallback(() => {
		setShowSuccessModal(false);
	}, []);

	const isFormLoading = isBillingLoading || isStatesLoading || isUserLoading;

	return (
		<div className={styles.accountSettingsWrapper}>
			{showSuccessModal ? (
				<TraxModal
					confirmText={"OK"}
					onClose={handleCloseSuccessModal}
					onConfirm={handleCloseSuccessModal}
					showCancelButton={false}
					showConfirmationButtons={true}
					title={"Success"}
					type={"success"}
				>
					<p>{successMessage}</p>
				</TraxModal>
			) : null}
			<TraxPageHeader showSupportMessage title={"Account Settings"} />
			<div className={styles.accountSettingsContainer}>
				<Form
					key={isFormLoading ? "loading" : "loaded"}
					initialValues={initialValues}
					onSubmit={handleSubmit}
					render={(formRenderProps: FormRenderProps) => {
						const invoicedVia = formRenderProps.valueGetter(
							"invoicedVia",
						) as string;
						const billingInfoSame = formRenderProps.valueGetter(
							"billingInfoSame",
						) as boolean;
						return (
							<FormElement>
								<BillingAddressSync formRenderProps={formRenderProps} />
								<div className={styles.accountSettingsContainer}>
									<div className={styles.accountSettingsTitle}>
										Billing Settings
									</div>
									<div className={styles.accountSettingsBilling}>
										<div className={styles.accountSettingsBillingLeft}>
											<div className={styles.accountSettingsRow}>
												<ValidationInputField
													data={["Email", "Mail", "Fax"]}
													disabled={!canEditBillingSettings}
													fieldType={FieldType.DROPDOWN}
													id={"invoicedVia"}
													label={"Invoiced Via"}
													loading={isFormLoading}
													name={"invoicedVia"}
													required={true}
													validator={requiredDropdownValidator}
													wrapperStyle={{ minWidth: 170, width: "auto" }}
												/>
												{invoicedVia === "Email" && (
													<ValidationInputField
														disabled={!canEditBillingSettings}
														fieldType={FieldType.TEXTBOX}
														id={"invoicedViaEmail"}
														label={"Email"}
														loading={isFormLoading}
														name={"invoicedViaEmail"}
														required={true}
														validator={requiredFieldValidator}
														wrapperStyle={{ minWidth: 250 }}
													/>
												)}
												{invoicedVia === "Fax" && (
													<ValidationInputField
														disabled={!canEditBillingSettings}
														fieldType={FieldType.MASKEDTEXTBOX}
														id={"invoicedViaFax"}
														label={"Fax"}
														loading={isFormLoading}
														mask={"(000) 000-0000"}
														name={"invoicedViaFax"}
														placeholder={"(000) 000-0000"}
														required={true}
														validator={requiredFieldValidator}
														wrapperStyle={{ minWidth: 250 }}
													/>
												)}
											</div>
											<div className={styles.accountSettingsRow}>
												<ValidationInputField
													data={reimburseUnloadingOptions}
													dataItemKey={"value"}
													disabled={!canEditBillingSettings}
													fieldType={FieldType.DROPDOWN}
													id={"reimburseUnloading"}
													label={"Do you reimburse for loading?"}
													loading={isFormLoading}
													name={"reimburseUnloading"}
													required={true}
													textField={"text"}
													validator={requiredDropdownValidator}
													wrapperStyle={{ maxWidth: 185 }}
												/>
												<ValidationInputField
													data={booleanDropdownOptions}
													dataItemKey={"value"}
													disabled={!canEditBillingSettings}
													fieldType={FieldType.DROPDOWN}
													id={"requireBOL"}
													label={"Do you require POD/BOLs?"}
													loading={isFormLoading}
													name={"requireBOL"}
													required={true}
													textField={"text"}
													validator={requiredDropdownValidator}
													wrapperStyle={{ maxWidth: 185 }}
												/>
												<ValidationInputField
													data={booleanDropdownOptions}
													dataItemKey={"value"}
													disabled={!canEditBillingSettings}
													fieldType={FieldType.DROPDOWN}
													hint={
														"Do you require us to send you documentation to move your shipment?"
													}
													id={"requireLoadTenders"}
													label={"Do you require Load Tenders?"}
													loading={isFormLoading}
													name={"requireLoadTenders"}
													required={true}
													textField={"text"}
													validator={requiredDropdownValidator}
													wrapperStyle={{ maxWidth: 185 }}
												/>
											</div>
										</div>
										<div className={styles.accountSettingsBillingRight}>
											<ValidationInputField
												disabled={!canEditBillingSettings}
												fieldType={FieldType.TEXTAREA}
												id={"miscRequirements"}
												label={"Misc. Requirements"}
												loading={isFormLoading}
												maxLength={1000}
												name={"miscRequirements"}
												onChange={handleMiscReqChange}
												wrapperStyle={{ minWidth: 150 }}
											/>
											<Hint direction={"end"}>
												{miscReqLength.length} / {miscReqMaxLength}
											</Hint>
										</div>
									</div>
								</div>
								<div
									className={`${styles.accountSettingsContainer} ${styles.accountSettingsAddressContainer}`}
								>
									<div className={styles.accountSettingsAddresses}>
										<div className={styles.accountSettingsMailingAddress}>
											<div className={styles.accountSettingsTitle}>
												Mailing Address
											</div>
											<AccountSettingsAddress
												disabled={!canEditAddresses}
												fieldPrefix={"mailing"}
												loading={isFormLoading}
												stateData={stateAbbreviations}
											/>
										</div>
										<div className={styles.accountSettingsBillingAddress}>
											<div className={styles.accountSettingsTitle}>
												Billing Address
											</div>
											<AccountSettingsAddress
												disabled={!canEditAddresses || billingInfoSame}
												fieldPrefix={"billing"}
												loading={isFormLoading}
												stateData={stateAbbreviations}
											/>
										</div>
									</div>
									<div className={styles.accountSettingsBillingInfoSame}>
										<ValidationInputField
											disabled={!canEditAddresses}
											fieldType={FieldType.CHECKBOX}
											id={"billingInfoSame"}
											label={
												"My billing information is the same as my mailing address"
											}
											loading={isFormLoading}
											name={"billingInfoSame"}
										/>
									</div>
								</div>
								<div className={styles.accountSettingSave}>
									<ConsentStatement
										actionLabel={
											updateMutation.isPending ? "Saving..." : "Save"
										}
									/>
									<Button
										disabled={
											(!canEditAddresses && !canEditBillingSettings) ||
											!formRenderProps.allowSubmit ||
											updateMutation.isPending
										}
										onClick={formRenderProps.onSubmit}
										variant={"primary"}
									>
										{updateMutation.isPending ? "Saving..." : "Save"}
									</Button>
								</div>
							</FormElement>
						);
					}}
					validator={formValidator}
				/>
			</div>
		</div>
	);
};
